import React, { useEffect, useMemo, useState } from 'react';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { useRateCard } from '../../hooks/useRateCard';
import { useAuth } from '../../hooks/useAuth';
import { ChevronDown, Truck, Plane } from 'lucide-react';
import { Loader } from '../../components/ui/Loader';
import Tooltip from '../../components/ui/Tooltip';
import GstToggle from '../../components/ui/GstToggle';

const RateCardPage = () => {
  const { user } = useAuth();
  const { data: rateCards, loading } = useRateCard(user?.id);

  // CHANGE 1: Use a Record to track which couriers are COLLAPSED
  // By default, it's empty, so everything is open.
  const [collapsedCouriers, setCollapsedCouriers] = useState<
    Record<string, boolean>
  >({});

  const [selectedCourier, setSelectedCourier] = useState<string>('all');
  const [selectedMode, setSelectedMode] = useState<string>('all');
  const [selectedWeight, setSelectedWeight] = useState<string>('all');
  const [inclusiveGst, setInclusiveGst] = useState<boolean>(false);
  const modeOptions = ['Air', 'Surface'];

  const formatRate = (val: number | null | undefined) => {
    if (val == null) return '-';
    if (val === 0) return '0';
    return Number(val.toFixed(2)).toString();
  };

  const formatCodRate = (val: number | null | undefined) => {
    const rate = formatRate(val);
    return rate === '-' ? '-' : `₹${rate}`;
  };

  const couriers = useMemo(() => {
    const filtered = rateCards.filter((r) => {
      if (selectedCourier !== 'all' && r.courierName !== selectedCourier)
        return false;
      if (selectedMode !== 'all' && r.mode.replace('By ', '') !== selectedMode)
        return false;
      if (
        selectedWeight !== 'all' &&
        `${r.baseWeightSlab} kg` !== selectedWeight
      )
        return false;
      return true;
    });

    const courierMap = new Map<string, any[]>();
    filtered.forEach((item) => {
      const courierName = item.courierName ?? 'Unknown';
      if (!courierMap.has(courierName)) courierMap.set(courierName, []);
      courierMap.get(courierName)!.push(item);
    });

    return Array.from(courierMap.entries()).map(([courierName, records]) => {
      const rowMap = new Map<string, any>();

      records.forEach((r) => {
        const rowKey = `${r.mode}-${r.baseWeightSlab}-${r.addlWeightSlab}`;
        if (!rowMap.has(rowKey)) {
          rowMap.set(rowKey, {
            courier: courierName,
            mode: r.mode.replace('By ', ''),
            baseWeightSlab: r.baseWeightSlab ?? 0,
            addlWeightSlab: r.addlWeightSlab ?? 0,
            weight: `${r.baseWeightSlab} kg`,
            addWeight: `Add ${r.addlWeightSlab} kg`,
            zones: [
              { fwd: '-', addl: '-', rto: '-' },
              { fwd: '-', addl: '-', rto: '-' },
              { fwd: '-', addl: '-', rto: '-' },
              { fwd: '-', addl: '-', rto: '-' },
              { fwd: '-', addl: '-', rto: '-' },
            ],
            cod: `${formatCodRate(r.codCharges)} | ${formatRate(r.codPercent)}%`,
            fuel: `${formatRate(r.fuelSurcharge ?? 0)}%`,
            other: `${formatRate(r.otherCharges ?? 0)}%`,
          });
        }
        const row = rowMap.get(rowKey);

        // Priority update for non-zero values because API can return multiple overlapping service records
        if (r.codCharges > 0 || r.codPercent > 0) {
          row.cod = `${formatCodRate(r.codCharges)} | ${formatRate(r.codPercent)}%`;
        }
        if (r.fuelSurcharge > 0 || r.fuelSurchargeAddl > 0) {
          row.fuel = `${formatRate(r.fuelSurcharge)}%`;
        }
        if (r.otherCharges > 0) {
          row.other = `${formatRate(r.otherCharges)}%`;
        }

        const zoneIndexMap: Record<string, number> = {
          'zone-1': 0,
          'zone-2': 1,
          'zone-3': 2,
          'zone-4': 3,
          'zone-5': 4,
        };
        const index = zoneIndexMap[r.zone];
        if (index !== undefined) {
          const taxRate = r.serviceTax && r.serviceTax > 0 ? r.serviceTax : 18;
          const taxMultiplier = inclusiveGst ? 1 + taxRate / 100 : 1;

          const effectiveFwd = r.baseRate ? r.baseRate * taxMultiplier : 0;
          const effectiveFwdAddl = r.baseRateAddl
            ? r.baseRateAddl * taxMultiplier
            : 0;

          const rawRto =
            r.rtoCharges && r.rtoCharges > 0 ? r.rtoCharges : r.baseRate || 0;
          const rawRtoAddl =
            r.rtoChargesAddl && r.rtoChargesAddl > 0
              ? r.rtoChargesAddl
              : r.baseRateAddl || 0;
          const effectiveRto = rawRto ? rawRto * taxMultiplier : 0;
          const effectiveRtoAddl = rawRtoAddl ? rawRtoAddl * taxMultiplier : 0;

          if (row.zones[index].fwd === '-' || r.baseRate > 0) {
            row.zones[index] = {
              fwd: `₹${formatRate(effectiveFwd)}`,
              addl: `₹${formatRate(effectiveFwdAddl)}`,
              rto: `₹${formatRate(effectiveRto)}`,
              rtoAddl: `₹${formatRate(effectiveRtoAddl)}`,
            };
          } else if (effectiveRto > 0 && row.zones[index].rto === '-') {
            row.zones[index].rto = `₹${formatRate(effectiveRto)}`;
            row.zones[index].rtoAddl = `₹${formatRate(effectiveRtoAddl)}`;
          }
        }
      });

      const sortedRows = Array.from(rowMap.values()).sort((a, b) => {
        if (a.baseWeightSlab !== b.baseWeightSlab)
          return a.baseWeightSlab - b.baseWeightSlab;
        if (a.addlWeightSlab !== b.addlWeightSlab)
          return a.addlWeightSlab - b.addlWeightSlab;
        return a.mode.localeCompare(b.mode);
      });

      return { name: courierName, rows: sortedRows };
    });
  }, [rateCards, selectedCourier, selectedMode, selectedWeight, inclusiveGst]);

  const courierOptions = useMemo(
    () =>
      Array.from(new Set(rateCards.map((r) => r.courierName))).filter(Boolean),
    [rateCards]
  );

  const weightOptions = useMemo(
    () =>
      Array.from(new Set(rateCards.map((r) => r.baseWeightSlab)))
        .filter((w) => w != null)
        .sort((a, b) => Number(a) - Number(b))
        .map((w) => `${w} kg`),
    [rateCards]
  );

  // CHANGE 2: Updated the reset logic to clear collapsed states if filters change
  useEffect(() => {
    setCollapsedCouriers({});
  }, [selectedCourier, selectedMode, selectedWeight]);

  // CHANGE 3: Toggle function logic
  const toggleCourier = (name: string) => {
    setCollapsedCouriers((prev) => ({
      ...prev,
      [name]: !prev[name], // Toggles true/false
    }));
  };

  return (
    <div className="py-6 p-4 lg:px-0 lg:py-10">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Rate Card</h1>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            Check shipping rates and charges based on your service type,
            courier, weight, and zone.
          </div>
        </div>
        <GstToggle enabled={inclusiveGst} onChange={setInclusiveGst} />
      </div>

      {inclusiveGst && (
        <div className="bg-teal/10 border border-teal/20 text-teal-900 text-xs sm:text-sm px-4 py-2.5 rounded-xl flex items-center justify-between shadow-xs mb-4">
          <div className="flex items-center gap-2 font-medium">
            <span className="h-2 w-2 rounded-full bg-teal shrink-0 animate-pulse"></span>
            Showing all zone rates inclusive of 18% GST (Service Tax).
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Select
          className="w-48"
          value={selectedCourier}
          onChange={(e) => setSelectedCourier(e.target.value)}
          placeholder="All Couriers"
          options={[
            { label: 'All Couriers', value: 'all' },
            ...courierOptions.map((c) => ({ label: c, value: c })),
          ]}
        />

        <Select
          className="w-40"
          value={selectedMode}
          onChange={(e) => setSelectedMode(e.target.value)}
          placeholder="All Modes"
          options={[
            { label: 'All Modes', value: 'all' },
            { label: 'Air', value: 'Air' },
            { label: 'Surface', value: 'Surface' },
          ]}
        />

        <Select
          className="w-40"
          value={selectedWeight}
          onChange={(e) => setSelectedWeight(e.target.value)}
          placeholder="All Weights"
          options={[
            { label: 'All Weights', value: 'all' },
            ...weightOptions.map((w) => ({ label: w, value: w })),
          ]}
        />
      </div>

      {/* Festive Peak Surcharge Info */}
      <div className="bg-red-50 border border-red-200 text-red-800 text-xs sm:text-sm px-4 py-3 rounded-xl flex items-start sm:items-center gap-3 shadow-sm mb-6">
        <span className="h-2 w-2 rounded-full bg-red-500 shrink-0 mt-1.5 sm:mt-0"></span>
        <span className="font-medium">
          Delhivery Peak Surcharge: Surface ₹3.5 &amp; Air ₹5.5 per shipment due
          to festive season.
        </span>
      </div>

      <div className="overflow-x-auto">
        <div className="space-y-3">
          {/* Table Header */}
          <div className="hidden lg:block bg-teal border rounded-xl px-3 xl:px-5 py-2 shadow-md">
            <div className="grid grid-cols-11 gap-2 xl:gap-4 text-xs xl:text-sm items-center font-bold text-white text-center">
              <div>Courier</div>
              <div>Mode</div>
              <div>Weight</div>

              <div className="flex flex-col items-center justify-center">
                <div className="flex flex-row">
                  Zone 1
                  <Tooltip text="Within City" />
                </div>
                <span className="text-xs font-normal">FWD | RTO</span>
              </div>

              <div className="flex flex-col items-center justify-center">
                <div className="flex flex-row">
                  Zone 2
                  <Tooltip text="Within State" />
                </div>
                <span className="text-xs font-normal">FWD | RTO</span>
              </div>

              <div className="flex flex-col items-center justify-center">
                <div className="flex flex-row">
                  Zone 3
                  <Tooltip text="Metro to Metro" />
                </div>
                <span className="text-xs font-normal">FWD | RTO</span>
              </div>

              <div className="flex flex-col items-center justify-center">
                <div className="flex flex-row">
                  Zone 4
                  <Tooltip text="Rest of India" />
                </div>
                <span className="text-xs font-normal">FWD | RTO</span>
              </div>

              <div className="flex flex-col items-center justify-center">
                <div className="flex flex-row">
                  Zone 5
                  <Tooltip text="NE & J&K" />
                </div>
                <span className="text-xs font-normal">FWD | RTO</span>
              </div>

              <div className="flex items-center justify-center">
                COD
                <Tooltip text="INR | %" />
              </div>

              <div className="flex items-center justify-center">
                Fuel Surcharge
                <Tooltip text="%" />
              </div>

              <div>DPH (%)</div>
            </div>
          </div>

          {loading && <Loader text="Loading rates" />}

          {couriers.map((courier) => {
            // CHANGE 4: Logic check - it is open if it is NOT in the collapsed record
            const isExpanded = !collapsedCouriers[courier.name];

            return (
              <div key={courier.name} className="border bg-card rounded-xl">
                <button
                  onClick={() => toggleCourier(courier.name)}
                  // className="w-full flex justify-between rounded-lg items-center px-6 py-4 bg-[#F5FCFC] text-lg text-text font-bold"
                  className="w-full flex justify-between rounded-lg items-center px-6 py-2 bg-teal/5 text-lg text-text font-bold"
                >
                  {courier.name}
                  <ChevronDown
                    className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                  />
                </button>

                {isExpanded && courier.rows.length > 0 && (
                  <div className="border-t animate-in fade-in duration-300">
                    {courier.rows.map((row, i) => (
                      <div
                        key={i}
                        className="border-b last:border-b-0 px-2 py-3.5 lg:grid lg:grid-cols-11 items-center lg:gap-2 xl:gap-4 lg:px-3 xl:px-5 lg:text-center text-xs xl:text-sm space-y-3 lg:space-y-0"
                      >
                        <div>
                          <span className="lg:hidden text-text3">
                            Courier:{' '}
                          </span>
                          {row.courier}
                        </div>
                        <div>
                          <span className="lg:hidden text-text3">Mode: </span>
                          <span className="flex items-center justify-center gap-2">
                            {row.mode === 'Surface' ||
                            row.mode === 'Surface (PE)' ? (
                              <Truck className="w-8 h-6 text-gray-900" />
                            ) : row.mode === 'Air' ? (
                              <Plane className="w-8 h-6 text-gray-900" />
                            ) : (
                              row.mode
                            )}
                          </span>
                        </div>
                        <div>
                          <span className="lg:hidden text-text3">Weight: </span>
                          <span className="font-medium text-gray-900">
                            {row.weight}
                          </span>
                          <div className="text-gray-500 mt-2.5">
                            {row.addWeight}
                          </div>
                        </div>
                        {row.zones.map((z: any, zi: number) => (
                          <div
                            key={zi}
                            className="flex flex-col lg:items-center"
                          >
                            <span className="lg:hidden text-text3">{`Zone ${zi + 1}: `}</span>
                            <div className="font-medium text-gray-700">
                              {z.fwd} | {z.rto}
                            </div>
                            <div className="text-gray-500 mt-2.5">
                              {z.addl} | {z.rtoAddl}
                            </div>
                          </div>
                        ))}
                        <div className="text-gray-500 font-medium">
                          <span className="lg:hidden text-text3">COD: </span>
                          {row.cod}
                        </div>
                        <div className="text-gray-500 font-medium">
                          <span className="lg:hidden text-text3">Fuel: </span>
                          {row.fuel}
                        </div>
                        <div className="text-gray-500 font-medium">
                          <span className="lg:hidden text-text3">Other: </span>
                          {row.other}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RateCardPage;
